import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO, { SEO_DATA } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ArrowRight } from 'lucide-react';

const faqs = [
  {
    category: 'The Process',
    items: [
      { q: 'How does the process work?', a: 'It\'s simple: Submit your intake form with your current resume and tell us what help you need. We review your materials personally, confirm the right scope and pricing, then send your Stripe invoice. Work begins after payment is received, and we deliver with revisions included within the agreed scope.' },
      { q: 'Do I pay right away?', a: 'No. You won\'t be charged until your materials are reviewed and the scope is confirmed. We send a Stripe invoice only after we\'ve determined the right service for your needs.' },
      { q: 'Why is my resume reviewed first?', a: 'Every resume is different. By reviewing your materials first, we can confirm the right service level, identify any special needs, and give you an accurate scope and timeline. This ensures you get exactly what you need—no more, no less.' },
      { q: 'How will I receive my invoice?', a: 'Once we\'ve reviewed your intake and confirmed the scope, we\'ll send you a Stripe invoice via email. You can pay securely with any major credit card.' },
      { q: 'When does work begin?', a: 'Work begins after your payment is received. Once paid, you\'ll be placed in queue and we\'ll start on your materials according to the confirmed turnaround time.' },
    ]
  },
  {
    category: 'Revisions & Scope',
    items: [
      { q: 'Are revisions included?', a: 'Yes. Reasonable revisions within the agreed scope are included with every project. We work with you to refine the final result until you\'re satisfied within the original service scope.' },
      { q: 'What does "reasonable revisions within scope" mean?', a: 'It means we\'ll make adjustments to improve the materials we delivered—tweaking wording, adjusting emphasis, fixing any issues. It does not include complete rewrites for different industries, adding new job experiences not discussed, or changes that significantly expand the original scope.' },
      { q: 'What if I need changes beyond the original scope?', a: 'If you need additional services beyond what was originally agreed (like adding LinkedIn optimization to a resume-only project), we\'ll discuss and quote those separately. We\'re always transparent about scope.' },
    ]
  },
  {
    category: 'Services & Pricing',
    items: [
      { q: 'Which service should I choose?', a: 'Let us help! Submit your intake and tell us your situation. We\'ll review your materials and recommend the right service level. You\'re never locked into a package until we\'ve confirmed the scope together.' },
      { q: 'What if I also need LinkedIn or cover letter help?', a: 'Just mention it in your intake form. We\'ll include any add-ons in your confirmed scope before sending the invoice. Many clients bundle services for a complete package.' },
      { q: 'Is this a subscription?', a: 'No. All services are one-time payments. Pay once, get your materials, done. No recurring charges, no hidden fees.' },
      { q: 'What payment methods do you accept?', a: 'We accept all major credit cards through Stripe. Your payment information is handled securely by Stripe and never stored on our servers.' },
    ]
  },
  {
    category: 'Turnaround & Delivery',
    items: [
      { q: 'How long does turnaround take?', a: 'Most projects are completed within 24-72 hours after payment, depending on the service level. Exact timing is confirmed before we send your invoice so you know what to expect.' },
      { q: 'What if I need it faster?', a: 'Let us know your deadline in the intake form. We\'ll do our best to accommodate rush requests and will confirm whether priority turnaround is possible before invoicing.' },
      { q: 'What format will I receive?', a: 'You\'ll receive both PDF and DOCX formats so you can use your resume immediately and make minor edits later if needed.' },
      { q: 'How will I receive my finished materials?', a: 'We\'ll deliver your completed materials through your client portal and notify you by email. You can download everything directly from your portal.' },
    ]
  },
  {
    category: 'Application Support',
    items: [
      { q: 'What is Application Support?', a: 'Application Support is our hands-on job application service. We source job opportunities matching your profile, you approve each one, and we submit applications on your behalf.' },
      { q: 'Do I need to share my job board passwords?', a: 'Never. We never request or store your login credentials. Applications are submitted through standard channels on your behalf.' },
      { q: 'Can I reject jobs you find?', a: 'Absolutely. You approve every job before we apply. We never submit an application without your explicit approval.' },
      { q: 'Do you guarantee interviews?', a: 'No. We handle the application process professionally, but interview outcomes depend on many factors beyond our control including the employer\'s needs, competition, and market conditions.' },
    ]
  },
  {
    category: 'General Questions',
    items: [
      { q: 'What makes Resume Surgeon different?', a: 'We combine real expertise with a human-first process. Every project is personally reviewed before billing, scope is confirmed before we charge you, and we work with you on revisions within scope. No templates, no AI-generated content—just expert resume professionals who care about your success.' },
      { q: 'Who works on my resume?', a: 'Your resume is handled by experienced professionals with backgrounds in HR, recruiting, and career coaching. Every project is personally reviewed—you\'re never just a number.' },
      { q: 'Do you guarantee interviews or job offers?', a: 'No. We optimize your resume to pass ATS systems and impress human readers, but many factors affect job search outcomes. We cannot guarantee interviews or employment.' },
    ]
  },
  {
    category: 'Privacy & Refunds',
    items: [
      { q: 'Is my information safe?', a: 'Yes. Your resume and personal information are used solely to deliver your services. We never sell or share your data with third parties.' },
      { q: 'How long do you keep my files?', a: 'Files are retained for 90 days after project completion to support any follow-up requests, then automatically deleted.' },
      { q: 'What\'s your refund policy?', a: 'Since we don\'t charge until scope is confirmed and you\'ve agreed to proceed, refunds are limited. If we fail to deliver within the agreed timeframe AND you choose not to continue, we\'ll discuss a refund. Once work is delivered, no refunds are available as the service has been rendered.' },
    ]
  }
];

export default function FAQPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO {...SEO_DATA.faq} />
      <Header />
      
      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="max-w-4xl mx-auto px-6 text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit' }}>
            Frequently Asked Questions
          </h1>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Everything you need to know before getting started. Every project is personally reviewed before scope and pricing are confirmed.
          </p>
        </section>

        {/* FAQ Sections */}
        <section className="max-w-4xl mx-auto px-6">
          <div className="space-y-12">
            {faqs.map((section, idx) => (
              <div key={idx} data-testid={`faq-section-${idx}`}>
                <h2 className="text-xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Outfit' }}>
                  {section.category}
                </h2>
                <Accordion type="single" collapsible className="space-y-3">
                  {section.items.map((faq, i) => (
                    <AccordionItem 
                      key={i} 
                      value={`${idx}-${i}`} 
                      className="border border-slate-200 rounded-xl px-6 bg-white"
                      data-testid={`faq-item-${idx}-${i}`}
                    >
                      <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline py-4">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-600 pb-4">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-3xl mx-auto px-6 mt-16 text-center">
          <Card className="p-8 rounded-3xl bg-slate-900 text-white">
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
              Ready to Get Started?
            </h3>
            <p className="text-slate-300 mb-6">
              Submit your intake and we'll review your materials personally before confirming the right scope and pricing.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/start')}
                className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8"
                data-testid="faq-start-btn"
              >
                Start Your Intake
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                variant="outline"
                className="border-slate-600 text-white hover:bg-slate-800 rounded-full"
                onClick={() => navigate('/pricing')}
                data-testid="faq-pricing-btn"
              >
                View Services & Pricing
              </Button>
            </div>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
}
